/** Accessible learner file dropzone with a consistent browse trigger. */
import type { CSSProperties, ReactNode } from 'react';
import {
  DropZone as AriaDropZone,
  isFileDropItem,
  type DropItem,
  type DropZoneProps as AriaDropZoneProps,
} from 'react-aria-components';
import { Upload } from 'lucide-react';
import { cn } from '@/foundation/utils/cn';
import { Button } from './Button';
import { FileTrigger } from './FileTrigger';

export interface FileDropZoneProps extends Omit<
  AriaDropZoneProps,
  'children' | 'className' | 'style' | 'onDrop'
> {
  acceptedFileTypes?: readonly string[];
  browseLabel?: ReactNode;
  className?: string;
  description?: ReactNode;
  icon?: ReactNode;
  label?: ReactNode;
  meta?: ReactNode;
  onFileSelect: (file: File) => void;
  style?: CSSProperties;
}

function getDropZoneStyle(
  isDropTarget: boolean,
  isFocusVisible: boolean,
  isDisabled: boolean,
  style?: CSSProperties
): CSSProperties {
  return {
    alignItems: 'center',
    background: isDropTarget ? 'var(--color-accent-subtle)' : 'var(--color-surface-muted)',
    border: `2px dashed ${isDropTarget ? 'var(--color-accent)' : 'var(--color-surface-border)'}`,
    borderRadius: 'var(--radius-lg)',
    boxShadow: isFocusVisible ? '0 0 0 3px var(--color-accent-subtle)' : undefined,
    color: 'var(--color-text-muted)',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'var(--font-sans)',
    gap: 8,
    justifyContent: 'center',
    minHeight: 160,
    opacity: isDisabled ? 0.5 : 1,
    outline: 'none',
    padding: 'var(--space-4)',
    textAlign: 'center',
    transition:
      'background var(--motion-fast) var(--motion-ease-out), border-color var(--motion-fast) var(--motion-ease-out), box-shadow var(--motion-fast) var(--motion-ease-out)',
    ...style,
  };
}

export function FileDropZone({
  acceptedFileTypes,
  browseLabel = 'browse',
  className,
  description,
  icon,
  label = 'Drop a file',
  meta,
  onFileSelect,
  style,
  ...props
}: FileDropZoneProps) {
  const handleDrop: NonNullable<AriaDropZoneProps['onDrop']> = async (event) => {
    const fileItem: DropItem | undefined = event.items.find(isFileDropItem);
    if (!fileItem || !isFileDropItem(fileItem)) return;
    onFileSelect(await fileItem.getFile());
  };

  const handleSelect = (files: FileList | null) => {
    const file = files?.[0];
    if (file) onFileSelect(file);
  };

  return (
    <AriaDropZone {...props} className={cn('outline-none', className)} onDrop={handleDrop}>
      {({ isDropTarget, isFocusVisible, isDisabled }) => (
        <div style={getDropZoneStyle(isDropTarget, isFocusVisible, isDisabled, style)}>
          <span style={{ color: 'var(--color-text-muted)', lineHeight: 0 }}>
            {icon ?? <Upload size={20} strokeWidth={2} />}
          </span>

          <p
            style={{
              alignItems: 'center',
              color: 'var(--color-text-muted)',
              display: 'flex',
              flexWrap: 'wrap',
              fontSize: 'var(--text-sm)',
              gap: 4,
              justifyContent: 'center',
              margin: 0,
            }}
          >
            <span>{label}</span>
            <span>or</span>
            <FileTrigger acceptedFileTypes={acceptedFileTypes} onSelect={handleSelect}>
              <Button
                variant="ghost"
                size="sm"
                style={{
                  color: 'var(--color-accent-text)',
                  minHeight: 'auto',
                  padding: 0,
                }}
              >
                {browseLabel}
              </Button>
            </FileTrigger>
          </p>

          {meta && (
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', margin: 0 }}>
              {meta}
            </p>
          )}
          {description && (
            <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: 0 }}>
              {description}
            </p>
          )}
        </div>
      )}
    </AriaDropZone>
  );
}
